import { useMemo } from 'react';
import { Portfolio, PortfolioHolding, Stock } from '../types';
import { useAppContext } from '../context/AppContext';
import { HoldingsCalculationService } from '../services/holdingsCalculationService';
import { StockNameManager } from '../services/stockNameManager';

export interface PortfolioMetrics {
  totalValue: number;
  totalCost: number;
  totalGain: number;
  totalGainPercent: number;
  holdings: Array<PortfolioHolding & {
    stockName: string;
    currentPrice: number | null;
    totalCost: number;  // 添加 totalCost
    totalValue: number;
    gain: number;
    gainPercent: number;
    change: number;
    changePercent: number;
    isDataMissing: boolean;
  }>;
  missingDataCount: number;
}

/**
 * 計算投資組合指標的 hook
 * 使用統一的 HoldingsCalculationService 進行計算
 * 
 * 注意：此 hook 主要用於相容性，建議直接使用 HoldingsCalculationService
 */
export const usePortfolioMetrics = (portfolio: Portfolio | null): PortfolioMetrics | null => {
  const { state } = useAppContext();
  
  return useMemo(() => {
    if (!portfolio?.holdings || portfolio.holdings.length === 0) {
      return {
        totalValue: 0,
        totalCost: 0,
        totalGain: 0,
        totalGainPercent: 0,
        holdings: [],
        missingDataCount: 0,
      };
    }

    // 如果有交易記錄，直接使用統一的計算服務（過濾當前帳戶）
    if (state.transactions.length > 0) {
      const accountTransactions = state.transactions.filter(t => t.accountId === state.currentAccountId);
      const { currentHoldings, summary } = HoldingsCalculationService.calculateHoldingsFromTransactions(
        accountTransactions,
        state.stocks,
        { includeFees: true, includeHistorical: false }
      );

      // 建立股票查找 Map
      const stockMap = new Map<string, Stock>();
      state.stocks.forEach(stock => {
        stockMap.set(stock.symbol, stock);
      });

      // 轉換為 PortfolioMetrics 格式
      const holdingsWithDetails = currentHoldings.map(holding => {
        const stock = stockMap.get(holding.symbol);
        const isDataMissing = !stock;
        
        return {
          symbol: holding.symbol,
          quantity: holding.currentQuantity,
          avgCost: holding.avgCost,
          stockName: StockNameManager.getStockName(holding.symbol),
          currentPrice: holding.currentPrice,
          totalCost: holding.investmentCost, // 使用統一服務計算的投資成本
          totalValue: holding.marketValue,
          gain: holding.unrealizedGain,
          gainPercent: holding.unrealizedGainPercent,
          change: stock?.change || 0,
          changePercent: stock?.changePercent || 0,
          isDataMissing,
        };
      });

      const missingDataCount = holdingsWithDetails.filter(holding => holding.isDataMissing).length;

      return {
        totalValue: summary.totalCurrentValue,
        totalCost: summary.totalInvestmentCost,
        totalGain: summary.totalUnrealizedGain,
        totalGainPercent: summary.totalInvestmentCost > 0 ? (summary.totalUnrealizedGain / summary.totalInvestmentCost) * 100 : 0,
        holdings: holdingsWithDetails,
        missingDataCount,
      };
    }

    // 降級到簡單計算（當沒有交易記錄時）
    // 建立股票查找 Map
    const stockMap = new Map<string, Stock>();
    state.stocks.forEach(stock => {
      stockMap.set(stock.symbol, stock);
    });

    // 計算每個持股的詳細資料
    const holdingsWithDetails = portfolio.holdings.map(holding => {
      const stock = stockMap.get(holding.symbol);
      const currentPrice = stock?.currentPrice || null;
      const stockName = StockNameManager.getStockName(holding.symbol);
      const totalCost = holding.quantity * holding.avgCost; // 簡單計算總成本
      const totalValue = currentPrice !== null ? holding.quantity * currentPrice : 0;
      const gain = currentPrice !== null ? totalValue - totalCost : 0;
      const gainPercent = currentPrice !== null && totalCost > 0 ? (gain / totalCost) * 100 : 0;
      const isDataMissing = !stock;
      
      return {
        ...holding,
        stockName,
        currentPrice,
        totalCost,
        totalValue,
        gain,
        gainPercent,
        change: stock?.change || 0,
        changePercent: stock?.changePercent || 0,
        isDataMissing,
      };
    });

    // 計算總計
    const totalValue = holdingsWithDetails.reduce((sum, holding) => sum + holding.totalValue, 0);
    const totalCost = holdingsWithDetails.reduce((sum, holding) => sum + holding.totalCost, 0);
    const totalGain = totalValue - totalCost;
    const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;
    const missingDataCount = holdingsWithDetails.filter(holding => holding.isDataMissing).length;

    return {
      totalValue,
      totalCost,
      totalGain,
      totalGainPercent,
      holdings: holdingsWithDetails,
      missingDataCount,
    };
  }, [portfolio, state.stocks, state.transactions, state.currentAccountId]);
};
